// src/components/dashboard/Dashboard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

// -----------------------------
// Vanilla CSS (self-contained)
// -----------------------------
const DASH_CSS = `
:root {
  --db-bg:#f6f7f8; --db-card:#fff; --db-border:#e6e8eb; --db-text:#0f172a;
  --db-muted:#6b7280; --db-primary:#0f172a; --db-ok:#16a34a; --db-warn:#eab308; --db-bad:#dc2626;
}
.db-page{background:var(--db-bg);min-height:100vh;}
.db-container{max-width:1200px;margin:0 auto;padding:16px;}
.db-header{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:16px;}
.db-title{font-size:22px;font-weight:700;color:var(--db-text);}
.db-sub{font-size:13px;color:var(--db-muted);}
.db-actions{display:flex;gap:8px;flex-wrap:wrap;}
.btn-sm{padding:6px 10px;border-radius:10px;font-size:13px;}

.db-grid{display:grid;grid-template-columns:1fr;gap:16px;}
@media (min-width:1024px){.db-grid{grid-template-columns:1.1fr 1fr;}}

.db-card{background:var(--db-card);border:1px solid var(--db-border);border-radius:16px;overflow:hidden;}
.db-card-header{padding:12px 14px;border-bottom:1px solid var(--db-border);display:flex;align-items:center;justify-content:space-between;}
.db-card-title{font-weight:600;font-size:15px;}
.db-card-body{padding:14px;}

.db-stats{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;}
.db-stat{border:1px solid var(--db-border);border-radius:12px;padding:12px;background:#fff;}
.db-stat-label{font-size:12px;color:var(--db-muted);}
.db-stat-value{font-size:22px;font-weight:700;color:var(--db-text);}

.db-toolbar{display:flex;gap:8px;align-items:center;margin-bottom:10px;flex-wrap:wrap;}
.db-input{border:1px solid var(--db-border);background:#fff;border-radius:12px;padding:8px 12px;font-size:14px;min-width:240px;}
.db-select{border:1px solid var(--db-border);background:#fff;border-radius:12px;padding:8px 12px;font-size:14px;}

.db-table{width:100%;border-collapse:collapse;font-size:14px;}
.db-table thead th{text-align:left;font-weight:600;color:var(--db-muted);border-bottom:1px solid var(--db-border);padding:10px;}
.db-table tbody td{padding:10px;border-bottom:1px solid var(--db-border);vertical-align:top;}
.db-table tbody tr:hover{background:#fafafa;}

.db-pill{display:inline-flex;align-items:center;gap:6px;padding:4px 8px;border-radius:999px;font-size:12px;border:1px solid var(--db-border);color:#374151;background:#fff;}
.db-pill.ok{color:var(--db-ok);border-color:#bbf7d0;background:#f0fdf4;}
.db-pill.warn{color:var(--db-warn);border-color:#fde68a;background:#fffbeb;}
.db-pill.bad{color:var(--db-bad);border-color:#fecaca;background:#fef2f2;}

.db-meta{color:var(--db-muted);font-size:12px;}
.db-empty{color:var(--db-muted);font-size:14px;padding:12px;border:1px dashed var(--db-border);border-radius:12px;text-align:center;}

/* Pagination */
.db-pagination{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;margin-top:12px;}
.db-pageinfo{font-size:13px;color:var(--db-muted);}
.db-pagegroup{display:flex;gap:6px;flex-wrap:wrap;}
.pagebtn{appearance:none;border:1px solid var(--db-border);background:#fff;padding:6px 10px;border-radius:10px;font-size:13px;cursor:pointer;min-width:36px;text-align:center;}
.pagebtn[disabled]{opacity:.6;cursor:not-allowed;}
.pagebtn.active{background:var(--db-primary);border-color:var(--db-primary);color:#fff;}
.dashboard-article-image{width:200px;}
`;

// -----------------------------
// Hilfsfunktionen
// -----------------------------
function useInjectStyles(css, id = "db-dashboard-styles") {
    useEffect(() => {
        if (typeof document === "undefined") return;
        if (document.getElementById(id)) return;
        const style = document.createElement("style");
        style.id = id;
        style.textContent = css;
        document.head.appendChild(style);
    }, [css, id]);
}
function formatDateTimeDE(iso) {
    if (!iso) return "";
    try {
        const d = new Date(iso);
        const dat = d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
        const tim = d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
        return `${dat} · ${tim} Uhr`;
    } catch {
        return String(iso);
    }
}
function daysBetween(a, b) {
    const ms = Math.abs(+a - +b);
    return Math.floor(ms / 86400000);
}
function RoleBadge({ roleId }) {
    const map = { 1: "Admin", 2: "Redakteur", 3: "Moderator" };
    return <span className="db-meta">Rolle: {map[Number(roleId)] || "User"}</span>;
}
async function getJSON(url, token) {
    const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(await res.text());
    return res.json();
}
async function del(url, token) {
    const res = await fetch(url, { method: "DELETE", headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) throw new Error(await res.text());
    return res.json().catch(() => ({}));
}
// 🌟 Normalisierer: egal ob {data:[...]}, {items:[...]}, {news:[...]}, Array oder Objekt
function toArray(val) {
    if (Array.isArray(val)) return val;
    if (val && typeof val === "object") {
        if (Array.isArray(val.data)) return val.data;
        if (Array.isArray(val.items)) return val.items;
        if (Array.isArray(val.news)) return val.news;
        if (Array.isArray(val.rows)) return val.rows;
        if (Array.isArray(val.results)) return val.results;
        const values = Object.values(val);
        if (values.every(v => typeof v === "object")) return values;
    }
    return [];
}
// Intelligente Seitenliste (mit …)
function getPageNumbers(current, total) {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    if (current <= 4) return [1, 2, 3, 4, 5, "...", total];
    if (current >= total - 3) return [1, "...", total - 4, total - 3, total - 2, total - 1, total];
    return [1, "...", current - 1, current, current + 1, "...", total];
}

export default function Dashboard() {
    useInjectStyles(DASH_CSS);
    const { user, token, roleId } = useAuth();
    const authorized = [1, 2, 3].includes(Number(roleId));

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [allNews, setAllNews] = useState([]);      // immer Array
    const [currentNews, setCurrentNews] = useState([]); // immer Array

    const [query, setQuery] = useState("");
    const [sortKey, setSortKey] = useState("dateDesc"); // dateDesc | titleAsc

    // Pagination-States
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10); // 10 | 20 | 50


    // 👉 Sitemap-Status
    const [smLoading, setSmLoading] = useState(false);     // NEU
    const [smResult, setSmResult] = useState(null);        // NEU

    // 👉 Sitemap aus Dashboard triggern
    const generateSitemap = async () => {                  // NEU
        setSmLoading(true);
        setSmResult(null);
        try {
            const adminToken = "supergeheimes-token"
            const res = await fetch("/api/admin/sitemap", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${adminToken}`,
                },
            });
            const data = await res.json();
            if (!res.ok || !data.ok) throw new Error(data.error || "Fehler beim Generieren");
            setSmResult({ ok: true, urls: data.urls, output: data.output });
        } catch (e) {
            setSmResult({ ok: false, error: e.message || String(e) });
        } finally {
            setSmLoading(false);
        }
    };

    // Stats clientseitig
    const stats = useMemo(() => {
        const list = Array.isArray(allNews) ? allNews : toArray(allNews);
        const total = list.length;
        const last7 = list.filter(n => {
            const iso = n.date || n.publishedAt || n.updated_at;
            if (!iso) return false;
            return daysBetween(new Date(iso), new Date()) <= 7;
        }).length;
        const noSlug = list.filter(n => !n.site_name).length;
        return { total, last7, noSlug };
    }, [allNews]);

    useEffect(() => {
        if (!authorized) return;
        let ignore = false;
        async function load() {
            try {
                setLoading(true);
                setError("");
                const [all, cur] = await Promise.allSettled([
                    getJSON("/api/news", token),          // GET /
                    getJSON("/api/news/current", token),  // GET /current (kann Objekt sein)
                ]);

                if (ignore) return;

                const allArr = all.status === "fulfilled" ? toArray(all.value) : [];
                const curArr = cur.status === "fulfilled" ? toArray(cur.value) : [];

                setAllNews(allArr);
                setCurrentNews(curArr);

                if (process.env.NODE_ENV !== "production") {
                    console.log("[Dashboard] /api/news →", all.value);
                    console.log("[Dashboard] /api/news/current →", cur.value);
                    console.log("[Dashboard] Normalisiert:", { allArr, curArr });
                }
            } catch (e) {
                if (!ignore) setError(e.message || "Fehler beim Laden.");
            } finally {
                if (!ignore) setLoading(false);
            }
        }
        load();
        return () => { ignore = true; };
    }, [authorized, token]);

    // Gefiltert & sortiert (volle Liste, noch ohne Pagination)
    const filteredNews = useMemo(() => {
        const list = Array.isArray(allNews) ? [...allNews] : [...toArray(allNews)];
        const q = query.trim().toLowerCase();

        let filtered = list;
        if (q) {
            filtered = list.filter((n) =>
                (n.headline || n.title || "").toLowerCase().includes(q) ||
                (n.username || n.author || "").toLowerCase().includes(q) ||
                (n.tags ? String(n.tags).toLowerCase() : "").includes(q) ||
                (n.site_name || "").toLowerCase().includes(q)
            );
        }

        if (sortKey === "titleAsc") {
            filtered.sort((a, b) => (a.headline || a.title || "").localeCompare(b.headline || b.title || ""));
        } else {
            // dateDesc
            filtered.sort(
                (a, b) =>
                    new Date(b.date || b.publishedAt || b.updated_at || 0) -
                    new Date(a.date || a.publishedAt || a.updated_at || 0)
            );
        }

        return filtered;
    }, [allNews, query, sortKey]);

    // Pagination anwenden
    const totalResults = filteredNews.length;
    const totalPages = Math.max(1, Math.ceil(totalResults / pageSize));
    const currentPage = Math.min(page, totalPages);
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = Math.min(startIndex + pageSize, totalResults);
    const paginatedNews = filteredNews.slice(startIndex, endIndex);

    // Seite zurücksetzen, wenn Filter/Sortierung sich ändern
    useEffect(() => {
        setPage(1);
    }, [query, sortKey, pageSize]);

    // Seite korrigieren, falls nach Löschungen etc. die aktuelle Seite > totalPages ist
    useEffect(() => {
        if (page > totalPages) setPage(totalPages);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalPages]);

    const onDelete = async (site_name) => {
        if (!site_name) return alert("Kein site_name für Löschung vorhanden.");
        if (!window.confirm(`Artikel „${site_name}“ wirklich löschen?`)) return;
        try {
            await del(`/api/news/${encodeURIComponent(site_name)}`, token);
            setAllNews((prev) => (Array.isArray(prev) ? prev.filter((n) => n.site_name !== site_name) : []));
            setCurrentNews((prev) => (Array.isArray(prev) ? prev.filter((n) => n.site_name !== site_name) : []));
        } catch (e) {
            alert(e.message || "Löschen fehlgeschlagen.");
        }
    };

    if (!authorized) {
        return (
            <div className="db-page">
                <div className="db-container">
                    <div className="db-header">
                        <div>
                            <div className="db-title">Dashboard</div>
                            <div className="db-sub">Kein Zugriff. Erforderliche Rollen: Admin, Redakteur, Moderator.</div>
                        </div>
                    </div>
                    <div className="db-card">
                        <div className="db-card-body">
                            <p className="db-empty">Bitte melde dich mit einem berechtigten Konto an.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="db-page">
            <div className="db-container">
                <div className="db-header">
                    <div>
                        <div className="db-title">Redaktions-Dashboard</div>
                        <div className="db-sub">
                            Willkommen, {user?.username || "User"} · <RoleBadge roleId={roleId} />
                        </div>
                    </div>
                    <div className="db-actions">
                        <Link className="btn btn-primary" to="/editor/news">+ Neuen Artikel erstellen</Link>
                        <button className="btn" onClick={() => window.location.reload()}>Aktualisieren</button>
                        <button
                            className="btn"
                            onClick={generateSitemap}
                            disabled={smLoading}
                            title="Sitemap jetzt neu generieren"
                        >
                            {smLoading ? "Sitemap…" : "Sitemap generieren"}
                        </button>
                    </div>
                </div>

                {error && (
                    <div className="db-card" style={{ marginBottom: 16 }}>
                        <div className="db-card-body">
                            <span className="db-pill bad">Fehler</span> <span className="db-meta">{String(error)}</span>
                        </div>
                    </div>
                )}

                {smResult && (                                              /* NEU: Ergebnisbox */
                    <div className="db-card" style={{ marginBottom: 16 }}>
                        <div className="db-card-body">
                            {smResult.ok ? (
                                <>
                                    <span className="db-pill ok">Sitemap OK</span>{" "}
                                    <span className="db-meta">
                                        {smResult.urls} URLs · geschrieben nach <code>{smResult.output}</code>
                                    </span>
                                </>
                            ) : (
                                <>
                                    <span className="db-pill bad">Sitemap Fehler</span>{" "}
                                    <span className="db-meta">{String(smResult.error)}</span>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* Übersicht */}
                <section className="db-card">
                    <div className="db-card-header">
                        <div className="db-card-title">Übersicht</div>
                    </div>
                    <div className="db-card-body">
                        {loading ? (
                            <p>Lade Statistiken…</p>
                        ) : (
                            <div className="db-stats">
                                <div className="db-stat">
                                    <div className="db-stat-label">Gesamt</div>
                                    <div className="db-stat-value">{stats.total}</div>
                                </div>
                                <div className="db-stat">
                                    <div className="db-stat-label">Letzte 7 Tage</div>
                                    <div className="db-stat-value">{stats.last7}</div>
                                </div>
                                <div className="db-stat">
                                    <div className="db-stat-label">Ohne Permalink</div>
                                    <div className="db-stat-value">{stats.noSlug}</div>
                                </div>
                            </div>
                        )}
                    </div>
                </section>

                {/* Alle Artikel (mit Pagination) */}
                <section className="db-card" style={{ marginTop: 16 }}>
                    <div className="db-card-header">
                        <div className="db-card-title">Alle Artikel</div>
                        <div className="db-sub">{totalResults} Einträge gesamt</div>
                    </div>
                    <div className="db-card-body">
                        <div className="db-toolbar">
                            <input
                                className="db-input"
                                placeholder="Suchen (Titel, Autor, Tags, site_name)…"
                                value={query}
                                onChange={(e) => setQuery(e.target.value)}
                            />
                            <select className="db-select" value={sortKey} onChange={(e) => setSortKey(e.target.value)}>
                                <option value="dateDesc">Neueste zuerst</option>
                                <option value="titleAsc">Titel A–Z</option>
                            </select>
                            <select
                                className="db-select"
                                value={pageSize}
                                onChange={(e) => setPageSize(Number(e.target.value))}
                                aria-label="Seitengröße"
                            >
                                <option value={10}>10 / Seite</option>
                                <option value={20}>20 / Seite</option>
                                <option value={50}>50 / Seite</option>
                            </select>
                        </div>

                        {loading ? (
                            <p>Lade Artikel…</p>
                        ) : totalResults === 0 ? (
                            <div className="db-empty">Keine Artikel gefunden.</div>
                        ) : (
                            <>
                                <table className="db-table">
                                    <thead>
                                        <tr>
                                            <th>Image</th>
                                            <th>Titel</th>
                                            <th>Autor</th>
                                            <th>Datum</th>
                                            <th>Aktionen</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedNews.map((a) => (
                                            <tr key={a.id || a.site_name}>
                                                <td>
                                                    <img
                                                        src={`/news/bilder/${a.image}`}
                                                        alt={a.headline}
                                                        className="dashboard-article-image"
                                                    />
                                                </td>
                                                <td>
                                                    <div style={{ fontWeight: 600 }}>{a.headline || a.title}</div>
                                                    <div className="db-meta">
                                                        {Array.isArray(a.tags) ? a.tags.join(", ") : a.tags}
                                                        {a.tags ? " · " : ""}{a.site_name || "-"}
                                                    </div>
                                                </td>
                                                <td>{a.username || a.author || "-"}</td>
                                                <td>{formatDateTimeDE(a.date || a.publishedAt || a.updated_at)}</td>
                                                <td>
                                                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                                        {a.site_name ? (
                                                            <>
                                                                <Link className="btn btn-sm btn-primary" to={`/editor/news/${a.site_name}`}>Bearbeiten</Link>
                                                                <Link className="btn btn-sm btn-primary" to={`/news/${a.site_name}`}>Ansehen</Link>
                                                                <button className="btn btn-sm" onClick={() => onDelete(a.site_name)}>Löschen</button>
                                                            </>
                                                        ) : (
                                                            <button className="btn btn-sm" disabled>Kein site_name</button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>

                                {/* Pagination */}
                                <div className="db-pagination">
                                    <div className="db-pageinfo">
                                        Zeige <strong>{startIndex + 1}</strong>–<strong>{endIndex}</strong> von <strong>{totalResults}</strong>
                                    </div>
                                    <div className="db-pagegroup">
                                        <button
                                            className="pagebtn"
                                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                                            disabled={currentPage === 1}
                                        >
                                            ‹ Zurück
                                        </button>

                                        {getPageNumbers(currentPage, totalPages).map((n, i) =>
                                            n === "..." ? (
                                                <button key={`e${i}`} className="pagebtn" disabled>…</button>
                                            ) : (
                                                <button
                                                    key={n}
                                                    className={`pagebtn ${n === currentPage ? "active" : ""}`}
                                                    onClick={() => setPage(n)}
                                                >
                                                    {n}
                                                </button>
                                            )
                                        )}

                                        <button
                                            className="pagebtn"
                                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                            disabled={currentPage === totalPages}
                                        >
                                            Weiter ›
                                        </button>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </section>
            </div>
        </div>
    );
}
